/** Derive user-facing command outcome from agent status + result payload. */

export type CommandOutcome = 'pending' | 'running' | 'succeeded' | 'failed' | string;

const STDERR_FAILURE_MARKERS = [
  'cannot bind argument',
  'empty string',
  'convertto-securestring',
  'access is denied',
  'fullyqualifiederrorid',
  'categoryinfo',
  'exception calling',
  'was not found',
  'does not exist',
  'terminatingerror',
  'write-error',
];

function asResultObject(result: unknown): Record<string, unknown> {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return {};
  }
  return result as Record<string, unknown>;
}

function resultText(result: unknown, errorMessage?: string | null): string {
  const data = asResultObject(result);
  const parts = [
    data.output,
    data.stdout,
    data.stderr,
    data.error,
    errorMessage,
  ]
    .filter((p) => p != null && String(p).trim())
    .map(String);
  return parts.join('\n').trim();
}

function exitCode(result: unknown): number | null {
  const data = asResultObject(result);
  if (data.exit_code === undefined || data.exit_code === null) {
    return null;
  }
  const n = Number(data.exit_code);
  return Number.isFinite(n) ? n : null;
}

/** Map raw device_commands row to succeeded/failed/etc. */
export function classifyCommandOutcome(
  status?: string | null,
  result?: unknown,
  errorMessage?: string | null,
): CommandOutcome {
  const st = (status || '').trim().toLowerCase();
  if (st === 'pending' || st === 'queued') {
    return 'pending';
  }
  if (st === 'running' || st === 'executing' || st === 'in_progress') {
    return 'running';
  }
  if (['failed', 'error', 'cancelled', 'canceled', 'timeout', 'timed_out'].includes(st)) {
    return 'failed';
  }

  const code = exitCode(result);
  if (code !== null && code !== 0) {
    return 'failed';
  }

  if ((errorMessage || '').trim()) {
    return 'failed';
  }

  const blob = resultText(result, errorMessage).toLowerCase();
  if (STDERR_FAILURE_MARKERS.some((marker) => blob.includes(marker))) {
    return 'failed';
  }

  if (['completed', 'success', 'succeeded', 'done'].includes(st)) {
    return 'succeeded';
  }
  if (st) {
    return st;
  }
  return 'pending';
}

export function isCommandSucceeded(
  status?: string | null,
  result?: unknown,
  errorMessage?: string | null,
): boolean {
  return classifyCommandOutcome(status, result, errorMessage) === 'succeeded';
}

export function isCommandFailed(
  status?: string | null,
  result?: unknown,
  errorMessage?: string | null,
): boolean {
  return classifyCommandOutcome(status, result, errorMessage) === 'failed';
}

export function commandOutcomeLabel(outcome: CommandOutcome): string {
  switch (outcome) {
    case 'succeeded':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'running':
      return 'running';
    case 'pending':
      return 'pending';
    default:
      return String(outcome || 'pending');
  }
}
